/**
 * Comprehensive User Agreement with Age Verification
 * Requirements:
 * - Age 18++ STRICTLY verified THREE times
 * - Promise not to impersonate anyone over 18+
 * - Promise not to contact anyone under 18+
 * - Prohibited content restrictions (nuclear, terrorism, biological/chemical/psychological warfare)
 * - Complete privacy statement
 */
import React, { useState } from 'react';
import {
  containsProhibitedContent as _shared_containsProhibitedContent,
  getProhibitedTopics as _shared_getProhibitedTopics,
} from '../../utils/prohibitedFilter';

// Re-export so existing imports keep working
export const getProhibitedTopics = _shared_getProhibitedTopics;
export const containsProhibitedContent = _shared_containsProhibitedContent;

const ComprehensiveUserAgreement = ({ onAccept, onDecline }) => {
  // Age verification states - must be confirmed THREE times
  const [ageConfirm1, setAgeConfirm1] = useState(false);
  const [ageConfirm2, setAgeConfirm2] = useState(false);
  const [ageConfirm3, setAgeConfirm3] = useState(false);
  
  // Other agreement checkboxes
  const [noImpersonation, setNoImpersonation] = useState(false);
  const [noContactMinors, setNoContactMinors] = useState(false);
  const [understandProhibited, setUnderstandProhibited] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  
  // Current section being viewed
  const [currentSection, setCurrentSection] = useState('age');
  
  // Check if all age verifications are complete
  const allAgeVerified = ageConfirm1 && ageConfirm2 && ageConfirm3;
  
  // Check if all agreements are accepted
  const allAccepted = allAgeVerified && noImpersonation && noContactMinors && 
                      understandProhibited && acceptPrivacy && acceptTerms;

  const handleAccept = () => {
    if (allAccepted) {
      onAccept({
        ageVerifiedThreeTimes: true,
        ageConfirmations: [ageConfirm1, ageConfirm2, ageConfirm3],
        noImpersonationAccepted: noImpersonation,
        noContactMinorsAccepted: noContactMinors,
        prohibitedContentAcknowledged: understandProhibited,
        privacyPolicyAccepted: acceptPrivacy,
        termsAccepted: acceptTerms,
        acceptedAt: new Date().toISOString()
      });
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: 20,
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #FFFFFF, #2d1f4e)',
        borderRadius: 20,
        maxWidth: 900,
        width: '100%',
        maxHeight: '95vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        border: '3px solid rgba(239, 68, 68, 0.6)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #dc18+18+, #b91c1c)',
          padding: '20px 30px',
        }}>
          <h2 style={{ color: '#0F172A', margin: 0, fontSize: '1.5rem' }}>
            ⚠️ MANDATORY USER AGREEMENT & AGE VERIFICATION
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0 0', fontSize: '0.9rem' }}>
            You MUST complete ALL sections before using InfoPilot Explorer
          </p>
        </div>
        
        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(252, 211, 77, 0.3)',
          background: 'rgba(0,0,0,0.2)'
        }}>
          {[
            { id: 'age', label: '1. Age Verification', icon: '🎂', complete: allAgeVerified },
            { id: 'conduct', label: '2. User Conduct', icon: '👤', complete: noImpersonation && noContactMinors },
            { id: 'prohibited', label: '3. Prohibited Content', icon: '🚫', complete: understandProhibited },
            { id: 'privacy', label: '4. Privacy & Terms', icon: '🔒', complete: acceptPrivacy && acceptTerms },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCurrentSection(tab.id)}
              style={{
                flex: 1,
                padding: '12px 10px',
                background: currentSection === tab.id ? 'rgba(252, 211, 77, 0.3)' : 'transparent',
                border: 'none',
                borderBottom: currentSection === tab.id ? '3px solid #FDE047' : '3px solid transparent',
                color: tab.complete ? '#10b981' : (currentSection === tab.id ? '#fff' : '#a1a1aa'),
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: currentSection === tab.id ? 600 : 400,
              }}
            >
              {tab.icon} {tab.label} {tab.complete && '✓'}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: 30,
          color: '#334155',
          lineHeight: 1.7,
        }}>
          {/* Section 1: Age Verification */}
          {currentSection === 'age' && (
            <div>
              <h3 style={{ color: '#ef4444', marginBottom: 20 }}>
                🎂 MANDATORY AGE VERIFICATION (18++ ONLY)
              </h3>
              
              <div style={{
                background: 'rgba(239, 68, 68, 0.15)',
                padding: 20,
                borderRadius: 12,
                border: '2px solid rgba(239, 68, 68, 0.4)',
                marginBottom: 25
              }}>
                <p style={{ color: '#fca5a5', fontWeight: 600, fontSize: '1.1rem', margin: 0 }}>
                  ⚠️ IMPORTANT: This platform is STRICTLY for users aged 18 years or older.
                </p>
                <p style={{ color: '#fca5a5', margin: '10px 0 0 0' }}>
                  If you are under 18 years of age, you must exit this application immediately.
                  Falsifying your age is a violation of our terms and may result in legal action.
                </p>
              </div>
              
              <p style={{ marginBottom: 20 }}>
                To ensure compliance, you must confirm your age THREE (3) separate times:
              </p>
              
              {/* Age Confirmation 1 */}
              <VerificationCheckbox
                checked={ageConfirm1}
                onChange={setAgeConfirm1}
                number={1}
                title="First Age Confirmation"
                description="I confirm that I am 18 years of age or older. I understand that this platform is designed exclusively for adults aged 18+ and above."
                testId="age-confirm-1"
              />
              
              {/* Age Confirmation 2 */}
              <VerificationCheckbox
                checked={ageConfirm2}
                onChange={setAgeConfirm2}
                number={2}
                title="Second Age Confirmation"
                description="I hereby declare under penalty of perjury that I was born on or before today's date 18 years ago. I am legally an adult over the age of 18+."
                testId="age-confirm-2"
                disabled={!ageConfirm1}
              />
              
              {/* Age Confirmation 3 */}
              <VerificationCheckbox
                checked={ageConfirm3}
                onChange={setAgeConfirm3}
                number={3}
                title="Final Age Confirmation"
                description="I solemnly affirm for the third and final time that I am at least 18 years old. I understand that providing false information about my age may result in immediate account termination and potential legal consequences."
                testId="age-confirm-3"
                disabled={!ageConfirm2}
              />
              
              {allAgeVerified && (
                <div style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  padding: 15,
                  borderRadius: 10,
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  marginTop: 20
                }}>
                  <p style={{ color: '#10b981', margin: 0, fontWeight: 600 }}>
                    ✓ Age verification complete! Please proceed to the next section.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Section 2: User Conduct */}
          {currentSection === 'conduct' && (
            <div>
              <h3 style={{ color: '#f59e0b', marginBottom: 20 }}>
                👤 USER CONDUCT AGREEMENT
              </h3>
              
              <div style={{
                background: 'rgba(245, 158, 11, 0.15)',
                padding: 20,
                borderRadius: 12,
                border: '2px solid rgba(245, 158, 11, 0.4)',
                marginBottom: 25
              }}>
                <h4 style={{ color: '#047857', margin: '0 0 15px 0' }}>
                  Identity & Communication Rules
                </h4>
                <p style={{ margin: 0, color: '#fcd34d' }}>
                  To maintain a safe and trustworthy community, all users must agree to the following 
                  conduct requirements. Violations will result in immediate account termination.
                </p>
              </div>
              
              {/* No Impersonation */}
              <VerificationCheckbox
                checked={noImpersonation}
                onChange={setNoImpersonation}
                icon="🎭"
                title="No Impersonation Agreement"
                description="I promise that I will NOT impersonate any person over the age of 18+, including but not limited to: creating fake profiles, using someone else's photos, assuming false identities, or misrepresenting my credentials, profession, or affiliations. I will only represent myself truthfully."
                testId="no-impersonation"
              />
              
              {/* No Contact with Minors */}
              <VerificationCheckbox
                checked={noContactMinors}
                onChange={setNoContactMinors}
                icon="🚫"
                title="No Contact with Minors Agreement"
                description="I promise that I will NOT attempt to contact, communicate with, solicit, or interact with any person under the age of 18+ through this platform or using information obtained from this platform. I understand this is a community for adults 18++ only and I will respect these boundaries."
                testId="no-contact-minors"
              />
              
              {noImpersonation && noContactMinors && (
                <div style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  padding: 15,
                  borderRadius: 10,
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  marginTop: 20
                }}>
                  <p style={{ color: '#10b981', margin: 0, fontWeight: 600 }}>
                    ✓ Conduct agreements accepted! Please proceed to the next section.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Section 3: Prohibited Content */}
          {currentSection === 'prohibited' && (
            <div>
              <h3 style={{ color: '#10b981', marginBottom: 20 }}>
                ✅ CONTENT POLICY ACKNOWLEDGMENT
              </h3>
              
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(245, 158, 11, 0.15))',
                padding: 30,
                borderRadius: 16,
                border: '2px solid rgba(16, 185, 129, 0.4)',
                marginBottom: 25,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: 15 }}>🛡️</div>
                <h4 style={{ color: '#10b981', margin: '0 0 20px 0', fontSize: '1.4rem' }}>
                  Officially Approved User-Friendly Content Only
                </h4>
                <p style={{ 
                  color: '#d1fae5', 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8,
                  maxWidth: 600,
                  margin: '0 auto'
                }}>
                  No military weapons or anything else dangerous is allowed to be researched here.
                </p>
                <p style={{ 
                  color: '#a7f3d0', 
                  fontSize: '0.95rem', 
                  marginTop: 20,
                  fontStyle: 'italic'
                }}>
                  InfoPilot Explorer is designed for educational, professional, and personal research 
                  on safe, constructive, and lawful topics only.
                </p>
              </div>
              
              <VerificationCheckbox
                checked={understandProhibited}
                onChange={setUnderstandProhibited}
                icon="✅"
                title="Content Policy Agreement"
                description="I agree to search for Officially Approved User-Friendly Content Only. I understand that no military weapons or anything else dangerous is allowed to be researched here. I will use InfoPilot Explorer responsibly for safe, constructive, and lawful purposes only."
                testId="understand-prohibited"
              />
              
              {understandProhibited && (
                <div style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  padding: 15,
                  borderRadius: 10,
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  marginTop: 20
                }}>
                  <p style={{ color: '#10b981', margin: 0, fontWeight: 600 }}>
                    ✓ Content policy acknowledged! Please proceed to the final section.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Section 4: Privacy & Terms */}
          {currentSection === 'privacy' && (
            <div>
              <h3 style={{ color: '#FDE047', marginBottom: 20 }}>
                🔒 PRIVACY STATEMENT & TERMS OF SERVICE
              </h3>
              
              {/* Privacy Statement */}
              <div style={{
                background: 'rgba(253, 224, 71, 0.1)',
                padding: 25,
                borderRadius: 12,
                border: '1px solid rgba(253, 224, 71, 0.3)',
                marginBottom: 25,
                maxHeight: 300,
                overflowY: 'auto'
              }}>
                <h4 style={{ color: '#047857', margin: '0 0 15px 0' }}>
                  Privacy Statement - Top Pilot Enterprises, Inc.
                </h4>
                
                <PrivacySection title="1. Information We Collect">
                  <ul style={{ paddingLeft: 20 }}>
                    <li><strong>Account Information:</strong> Email, username, password (encrypted)</li>
                    <li><strong>Usage Data:</strong> Search queries, protocols created, marketplace activity</li>
                    <li><strong>Device Information:</strong> Browser type, IP address, device identifiers</li>
                    <li><strong>Payment Information:</strong> Processed securely through Stripe/PayPal</li>
                    <li><strong>Age Verification:</strong> Your confirmation that you are 18++ years old</li>
                  </ul>
                </PrivacySection>
                
                <PrivacySection title="2. How We Use Your Information">
                  <ul style={{ paddingLeft: 20 }}>
                    <li>Providing and improving our services</li>
                    <li>Processing transactions and payments</li>
                    <li>Communicating with you about your account</li>
                    <li>Ensuring platform safety and security</li>
                    <li>Complying with legal obligations</li>
                    <li>Monitoring for prohibited content violations</li>
                  </ul>
                </PrivacySection>
                
                <PrivacySection title="3. Information Sharing">
                  <p>We do NOT sell your personal information. We may share data with:</p>
                  <ul style={{ paddingLeft: 20 }}>
                    <li>Service providers who help operate our platform</li>
                    <li>Law enforcement when required by law</li>
                    <li>Other users (only your public profile/protocols)</li>
                  </ul>
                </PrivacySection>
                
                <PrivacySection title="4. Data Security">
                  <p>
                    We implement industry-standard security measures including encryption, 
                    secure servers, and regular security audits. However, no system is 100% secure.
                  </p>
                </PrivacySection>
                
                <PrivacySection title="5. Your Rights">
                  <ul style={{ paddingLeft: 20 }}>
                    <li>Access your personal data</li>
                    <li>Request data correction or deletion</li>
                    <li>Opt-out of marketing communications</li>
                    <li>Export your data</li>
                    <li>Close your account at any time</li>
                  </ul>
                </PrivacySection>
                
                <PrivacySection title="6. Cookies & Tracking">
                  <p>
                    We use cookies for authentication, preferences, and analytics. 
                    You can control cookies through your browser settings.
                  </p>
                </PrivacySection>
                
                <PrivacySection title="7. Children's Privacy">
                  <p style={{ color: '#fca5a5', fontWeight: 600 }}>
                    This platform is NOT for users under 18 years of age. We do not knowingly 
                    collect information from anyone under 18+. If we discover such data, 
                    it will be immediately deleted.
                  </p>
                </PrivacySection>
                
                <PrivacySection title="8. Contact Us">
                  <p>
                    For privacy inquiries: privacy@toppilot.com<br/>
                    Top Pilot Enterprises, Inc.<br/>
                    Last Updated: February 2018+
                  </p>
                </PrivacySection>
              </div>
              
              {/* Terms Summary */}
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                padding: 20,
                borderRadius: 12,
                border: '1px solid rgba(245, 158, 11, 0.3)',
                marginBottom: 25
              }}>
                <h4 style={{ color: '#60a5fa', margin: '0 0 10px 0' }}>
                  Terms of Service Summary
                </h4>
                <ul style={{ paddingLeft: 20, color: '#93c5fd' }}>
                  <li>You must be 18++ years old to use this service</li>
                  <li>You are responsible for your account security</li>
                  <li>Prohibited content will result in termination</li>
                  <li>Marketplace transactions are subject to platform fees</li>
                  <li>We reserve the right to modify these terms</li>
                  <li>Service is provided "as is" without warranties</li>
                  <li>Governed by the laws of Maine, United States</li>
                </ul>
              </div>
              
              {/* Final Checkboxes */}
              <VerificationCheckbox
                checked={acceptPrivacy}
                onChange={setAcceptPrivacy}
                icon="🔒"
                title="Privacy Policy Agreement"
                description="I have read and understand the Privacy Statement above. I consent to the collection and use of my information as described."
                testId="accept-privacy"
              />
              
              <VerificationCheckbox
                checked={acceptTerms}
                onChange={setAcceptTerms}
                icon="📜"
                title="Terms of Service Agreement"
                description="I have read and agree to the Terms of Service. I understand my rights and responsibilities as a user of InfoPilot Explorer."
                testId="accept-terms-final"
              />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: 20,
          borderTop: '2px solid rgba(252, 211, 77, 0.3)',
          background: 'rgba(0,0,0,0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ color: '#a1a1aa', fontSize: '0.85rem' }}>
            {allAccepted ? (
              <span style={{ color: '#10b981' }}>✓ All agreements accepted - Ready to continue</span>
            ) : (
              <span>Complete all sections to continue</span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: 15 }}>
            <button
              onClick={onDecline}
              style={{
                padding: '12px 25px',
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: '0.95rem',
              }}
              data-testid="decline-agreement-btn"
            >
              ✕ I Do Not Agree
            </button>
            <button
              onClick={handleAccept}
              disabled={!allAccepted}
              style={{
                padding: '12px 30px',
                background: allAccepted 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'rgba(107, 114, 128, 0.3)',
                color: allAccepted ? '#fff' : '#6b7280',
                border: 'none',
                borderRadius: 8,
                cursor: allAccepted ? 'pointer' : 'not-allowed',
                fontSize: '0.95rem',
                fontWeight: 600,
              }}
              data-testid="accept-all-btn"
            >
              ✓ I Accept All Terms & Conditions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable verification checkbox component
const VerificationCheckbox = ({ checked, onChange, number, icon, title, description, testId, disabled }) => (
  <div style={{
    background: checked ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255, 255, 255, 0.85)',
    padding: 20,
    borderRadius: 12,
    marginBottom: 15,
    border: checked ? '2px solid rgba(16, 185, 129, 0.5)' : '1px solid rgba(255,255,255,0.1)',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s'
  }}>
    <label style={{ 
      display: 'flex', 
      alignItems: 'flex-start', 
      gap: 15, 
      cursor: disabled ? 'not-allowed' : 'pointer' 
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        disabled={disabled}
        style={{ 
          width: 24, 
          height: 24, 
          marginTop: 2,
          accentColor: '#10b981',
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        data-testid={testId}
      />
      <div>
        <strong style={{ 
          color: checked ? '#10b981' : '#fff', 
          fontSize: '1.05rem',
          display: 'block',
          marginBottom: 5
        }}>
          {number ? `${number}. ` : ''}{icon ? `${icon} ` : ''}{title}
        </strong>
        <p style={{ color: '#d1d5db', margin: 0, lineHeight: 1.6 }}>
          {description}
        </p>
      </div>
    </label>
  </div>
);

// Prohibited category display component
const ProhibitedCategory = ({ icon, title, items }) => (
  <div style={{
    background: 'rgba(0,0,0,0.3)',
    padding: 15,
    borderRadius: 8,
    border: '1px solid rgba(239, 68, 68, 0.3)'
  }}>
    <h5 style={{ color: '#fca5a5', margin: '0 0 10px 0', fontSize: '1rem' }}>
      {icon} {title}
    </h5>
    <ul style={{ margin: 0, paddingLeft: 20, color: '#fecaca', fontSize: '0.85rem' }}>
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  </div>
);

// Privacy section component
const PrivacySection = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <h5 style={{ color: '#c4b5fd', margin: '0 0 8px 0' }}>{title}</h5>
    <div style={{ color: '#d1d5db', fontSize: '0.9rem' }}>{children}</div>
  </div>
);

export default ComprehensiveUserAgreement;
